/**
 * SCOPE (bid options / alternates) on an estimate: which scope a placement lands in, and the scope list the
 * board offers. The list comes from the linked Eos Estimating Workbook (DASHBOARDS' scope register, then any
 * other SCOPE already used in AREA/ZONE BOM); unlinked estimates get the usual names.
 *
 * Mirror of backend/apps/takeoff/quantities.py `resolve_scope`. Both test suites read
 * backend/tests/fixtures/scope_precedence.json and must give its answers.
 */

export const BASE_BID = 'BASE BID';

/** Offered when no workbook is linked (or its register only has BASE BID): the usual bid scopes. */
export const DEFAULT_SCOPES = ['BASE BID', 'ALTERNATE 1', 'ALTERNATE 2', 'ALTERNATE 3', 'OPTION 1', 'OPTION 2', 'OPTION 3'];

const clean = (v: unknown) => String(v ?? '').trim();

/** Precedence: the placement's own > its drawn zone's > its type's (item / assembly) > its frame's > the default. */
export function resolveScope(s: { placement?: string | null; zone?: string | null; type?: string | null; frame?: string | null; fallback?: string | null }): string {
  for (const v of [s.placement, s.zone, s.type, s.frame]) {
    const c = clean(v);
    if (c) return c;
  }
  return clean(s.fallback) || BASE_BID;
}

export interface ScopeChoices {
  /** In the workbook's scope register (priced on BID SUMMARY). */
  registered: string[];
  /** Usable, but not (yet) in the register: the workbook's SCOPE INTEGRITY check will flag them until added. */
  other: string[];
  /** True when the list came from a linked workbook. */
  fromWorkbook: boolean;
}

/** The dropdown's lists. `link` is the workbook link JSON (scopes / registered_scopes) or null when unlinked. */
export function scopeChoices(link: { linked?: boolean; scopes?: string[]; registered_scopes?: { name: string }[] } | null | undefined, inUse: readonly string[] = []): ScopeChoices {
  if (link?.linked) {
    const registered = (link.registered_scopes ?? []).map((r) => clean(r.name)).filter(Boolean);
    if (!registered.includes(BASE_BID)) registered.unshift(BASE_BID);
    const other: string[] = [];
    for (const v of [...(link.scopes ?? []), ...inUse, ...DEFAULT_SCOPES]) {
      const c = clean(v);
      if (c && !registered.includes(c) && !other.includes(c)) other.push(c);
    }
    return { registered, other, fromWorkbook: true };
  }
  const registered = [...DEFAULT_SCOPES];
  const other = inUse.map(clean).filter((c) => c && !registered.includes(c));
  return { registered, other: [...new Set(other)], fromWorkbook: false };
}

/** A scope is an option / alternate (anything but the base bid) — the budget tab highlights those totals. */
export function isOptionScope(scope: string): boolean {
  const c = clean(scope).toUpperCase();
  return !!c && c !== BASE_BID && c !== 'PROJECT TOTAL';
}
